import { useLingui } from '@lingui/react/macro';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsIcon from '@mui/icons-material/Settings';
import { AppBar, Box, Chip, Container, IconButton, Stack, Toolbar } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from '@tanstack/react-router';
import { memo, useCallback, useEffect, useState } from 'react';

import { useDialogStore } from '@/components/dialogs';
import { Logo } from '@/components/logo/logo';
import { NotificationsDrawer, useNotificationStore } from '@/feature/notifications';
import { useDepositButtonText } from '../../features/account';
import { OPEN_NOTIFICATIONS_EVENT } from '../../features/picture_in_picture/mainWindowBridge';
import useAppStore from '../../store/rootStore';
import { CustomConnectButton as ConnectButton } from '../rainbow/CustomConnectButton.tsx';
import { DESKTOP_BREAKPOINT, MOBILE_BREAKPOINT } from './constants';
import { DesktopActions } from './desktop_actions.tsx';
import { MobileDrawer } from './mobile_drawer.tsx';
import { NavHoverGroup } from './nav_hover_group';
import { NavigationTabs } from './navigation.tsx';
import { NotificationsButton } from './notifications_button.tsx';
import { PointsButton } from './points_button.tsx';
import { UserPreferencesDrawer } from './user_settings/preferences-drawer/preferences_drawer.tsx';

const StyledAppBar = styled(AppBar)(() => ({
  top: 0,
  boxShadow:
    '-1px -1px 1px 0px rgba(0, 0, 0, 0.16) inset, 0px 1px 8px 0px rgba(0, 0, 0, 0.12), 0px 3px 4px 0px rgba(0, 0, 0, 0.14), 0px 3px 3px -2px rgba(0, 0, 0, 0.20)',
  '& .MuiToolbar-root': {
    minHeight: 56,
  },
}));

const LogoLink = styled(Link)({
  display: 'flex',
  alignItems: 'center',
  textDecoration: 'none',
  transition: 'opacity 200ms',
  '&:hover': {
    opacity: 0.8,
  },
});

const MobileMenuButton = styled(IconButton)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down(DESKTOP_BREAKPOINT)]: {
    display: 'flex',
  },
}));

const DexHeader = memo(function DexHeader() {
  const { t } = useLingui();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [notificationsDrawerOpen, setNotificationsDrawerOpen] = useState(false);

  const isAtLeastAuthenticated = useAppStore((state) => state.isAtLeastAuthenticated());
  const isFullyAuthorized = useAppStore((state) => state.isFullyAuthorized());
  const depositButtonText = useDepositButtonText();
  const unreadCount = useNotificationStore((state) => state.list.filter((n) => !n.read).length);

  const languageSelectionEnabled = useAppStore((state) => state.config?.features?.languageSelection ?? false);
  const pictureInPictureEnabled = useAppStore((state) => state.config?.features?.pictureInPictureEnabled ?? false);
  const mainLogoLabel = useAppStore((state) => state.config?.features?.mainLogoLabel);
  const funBridgeEnabled = useAppStore((state) => !!state.config?.features?.funBridgeEnabled);

  const { openDialog } = useDialogStore((state) => state);

  const onDeposit = useCallback(() => {
    openDialog('depositCollateralDialog');
  }, [openDialog]);

  const onWithdraw = useCallback(() => {
    openDialog('withdrawDialog');
  }, [openDialog]);

  const handleSettingsMenuOpen = useCallback(() => {
    openDialog('userPreferencesDrawer');
  }, [openDialog]);

  const handleMobileDrawerToggle = useCallback(() => {
    setMobileDrawerOpen((prev) => !prev);
  }, []);

  const handleMobileDrawerClose = useCallback(() => {
    setMobileDrawerOpen(false);
  }, []);

  const handleNotificationsOpen = useCallback(() => {
    setNotificationsDrawerOpen(true);
  }, []);

  const handleNotificationsClose = useCallback(() => {
    setNotificationsDrawerOpen(false);
  }, []);

  useEffect(() => {
    const onOpenFromPip = () => {
      setNotificationsDrawerOpen(true);
    };
    window.addEventListener(OPEN_NOTIFICATIONS_EVENT, onOpenFromPip);
    return () => {
      window.removeEventListener(OPEN_NOTIFICATIONS_EVENT, onOpenFromPip);
    };
  }, []);

  return (
    <>
      <StyledAppBar position="static">
        <Container maxWidth={false}>
          <Toolbar disableGutters>
            <LogoLink to="/trade" aria-label={t({ comment: 'Header logo link to the trading page', message: 'Perpl home' })}>
              <Logo />
            </LogoLink>
            {mainLogoLabel && (
              <Chip
                label={mainLogoLabel}
                size="small"
                variant="outlined"
                clickable={false}
                sx={{
                  ml: 1,
                  pointerEvents: 'none',
                  cursor: 'default',
                  borderRadius: '4px',
                  // Optical nudge: the wordmark's descender pulls its visual centre ~1px below the toolbar centre.
                  position: 'relative',
                  top: '1px',
                }}
              />
            )}

            <NavigationTabs />
            <NavHoverGroup />

            <Box flexGrow={1} />

            <Stack direction="row" alignItems="center" spacing={1}>
              <DesktopActions
                canDeposit={isAtLeastAuthenticated}
                canWithdraw={isFullyAuthorized}
                canBridge={funBridgeEnabled && isAtLeastAuthenticated}
                depositButtonText={depositButtonText}
                onSettingsMenuOpen={handleSettingsMenuOpen}
                showLanguageFeature={languageSelectionEnabled}
                showPipFeature={isFullyAuthorized && pictureInPictureEnabled}
                onNotificationsOpen={handleNotificationsOpen}
                unreadCount={unreadCount}
              />

              <Stack direction="row" display={{ xs: 'flex', [MOBILE_BREAKPOINT]: 'none' }} alignItems="center" spacing={1}>
                <PointsButton />
                <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
                  <ConnectButton />
                </Box>
                <NotificationsButton
                  unreadCount={unreadCount}
                  onClick={handleNotificationsOpen}
                  aria-label={t({ comment: 'Header notifications button', message: 'Notifications' })}
                />
                <IconButton
                  onClick={handleSettingsMenuOpen}
                  aria-label={t({ comment: 'Header settings button', message: 'Settings' })}
                >
                  <SettingsIcon />
                </IconButton>
              </Stack>

              <MobileMenuButton
                onClick={handleMobileDrawerToggle}
                aria-label={t({ comment: 'Header button opening the mobile menu', message: 'Open mobile menu' })}
              >
                <MenuIcon />
              </MobileMenuButton>
            </Stack>
          </Toolbar>
        </Container>
      </StyledAppBar>

      <MobileDrawer
        open={mobileDrawerOpen}
        onClose={handleMobileDrawerClose}
        canDeposit={isAtLeastAuthenticated}
        canWithdraw={isFullyAuthorized}
        canBridge={funBridgeEnabled && isAtLeastAuthenticated}
        onDeposit={onDeposit}
        onWithdraw={onWithdraw}
        onSettingsMenuOpen={handleSettingsMenuOpen}
        showLanguageFeature={languageSelectionEnabled}
        onNotificationsOpen={handleNotificationsOpen}
        unreadCount={unreadCount}
      />

      <UserPreferencesDrawer />
      <NotificationsDrawer open={notificationsDrawerOpen} onClose={handleNotificationsClose} />
    </>
  );
});

export default DexHeader;
